import makeStyles from '@mui/styles/makeStyles';
import { Box, Grid, Theme, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import { useRecoilValue } from 'recoil';
import React from 'react';
import {
  GenericPayRateOverviewProps,
  Tier2DeliveryPay,
} from '../../../interfaces/PayRate';
import { sharedColors, useSharedStyles } from '../../../utilities/Styles';
import { locations as locationsAtom } from '../../../atoms/Location';
import {
  filterLocationById,
  getCurrencySymbolFromLocation,
  getTimeZoneByLocationId,
} from '../../../utilities/Locations';
import { multiClass } from '../../../utilities/Extensions';
import { formatDateTime } from '../../../utilities/Dates';

export const Tier2DeliveryPayOverview = (
  props: GenericPayRateOverviewProps,
) => {
  const classes = useStyles();
  const sharedClasses = useSharedStyles().classes;

  const locations = useRecoilValue(locationsAtom);

  const tier2DeliveryPay = props.payRate as Tier2DeliveryPay;

  const locationId = parseInt(tier2DeliveryPay.locationID, 10);
  const location = filterLocationById(locations, locationId);
  const timeZone =
    getTimeZoneByLocationId(
      locations,
      parseInt(tier2DeliveryPay.locationID, 10),
    ) || 'UTC';

  return (
    <Grid container spacing={1} className={classes.grid}>
      <Grid key='locationId' item xs={12}>
        <Box component='div' className={classes.fieldWrapper}>
          <Typography
            className={multiClass([sharedClasses.overline, classes.fieldName])}
            component={'div'}
          >
            Location Name
          </Typography>
          <Typography className={sharedClasses.body1} component={'div'}>
            {location?.name || '-'}
          </Typography>
        </Box>
      </Grid>
      <Grid key='startDate' item xs={12}>
        <Box component='div' className={classes.fieldWrapper}>
          <Typography
            component={'div'}
            className={multiClass([sharedClasses.overline, classes.fieldName])}
          >
            Start At
          </Typography>
          <Typography component='div' className={sharedClasses.body1}>
            {formatDateTime(tier2DeliveryPay.startAt, timeZone)}
          </Typography>
        </Box>
      </Grid>
      <Grid key='nonStarbucksRate' item xs={12}>
        <Box component='div' className={classes.fieldWrapper}>
          <Typography
            component={'div'}
            className={multiClass([sharedClasses.overline, classes.fieldName])}
          >
            Non Starbucks Amount
          </Typography>
          <Typography
            component={'div'}
            className={multiClass([
              sharedClasses.body1,
              classes.fieldValueWithUnit,
            ])}
          >
            {tier2DeliveryPay.nonStarbucks &&
              `${getCurrencySymbolFromLocation(
                location,
              )}${tier2DeliveryPay.nonStarbucks.toFixed(2)}`}
            {props.amountUnit && (
              <Box
                component='div'
                className={multiClass([sharedClasses.body2, classes.fieldUnit])}
              >
                {props.amountUnit}
              </Box>
            )}{' '}
          </Typography>
        </Box>
      </Grid>
      <Grid key='hotStarbucksRate' item xs={12}>
        <Box component='div' className={classes.fieldWrapper}>
          <Typography
            component={'div'}
            className={multiClass([sharedClasses.overline, classes.fieldName])}
          >
            Hot Starbucks Amount
          </Typography>
          <Typography
            component={'div'}
            className={multiClass([
              sharedClasses.body1,
              classes.fieldValueWithUnit,
            ])}
          >
            {tier2DeliveryPay.hotStarbucks &&
              `${getCurrencySymbolFromLocation(
                location,
              )}${tier2DeliveryPay.hotStarbucks.toFixed(2)}`}
            {props.amountUnit && (
              <Box
                component='div'
                className={multiClass([sharedClasses.body2, classes.fieldUnit])}
              >
                {props.amountUnit}
              </Box>
            )}{' '}
          </Typography>
        </Box>
      </Grid>
      <Grid key='coldStarbucksRate' item xs={12}>
        <Box component='div' className={classes.fieldWrapper}>
          <Typography
            component={'div'}
            className={multiClass([sharedClasses.overline, classes.fieldName])}
          >
            Cold Starbucks Amount
          </Typography>
          <Typography
            component={'div'}
            className={multiClass([
              sharedClasses.body1,
              classes.fieldValueWithUnit,
            ])}
          >
            {tier2DeliveryPay.coldStarbucks &&
              `${getCurrencySymbolFromLocation(
                location,
              )}${tier2DeliveryPay.coldStarbucks.toFixed(2)}`}
            {props.amountUnit && (
              <Box
                component='div'
                className={multiClass([sharedClasses.body2, classes.fieldUnit])}
              >
                {props.amountUnit}
              </Box>
            )}{' '}
          </Typography>
        </Box>
      </Grid>
      <Grid key='standardStarbucksRate' item xs={12}>
        <Box component='div' className={classes.fieldWrapper}>
          <Typography
            component={'div'}
            className={multiClass([sharedClasses.overline, classes.fieldName])}
          >
            Standard Starbucks Amount
          </Typography>
          <Typography
            component={'div'}
            className={multiClass([
              sharedClasses.body1,
              classes.fieldValueWithUnit,
            ])}
          >
            {tier2DeliveryPay.standardStarbucks &&
              `${getCurrencySymbolFromLocation(
                location,
              )}${tier2DeliveryPay.standardStarbucks.toFixed(2)}`}
            {props.amountUnit && (
              <Box
                component='div'
                className={multiClass([sharedClasses.body2, classes.fieldUnit])}
              >
                {props.amountUnit}
              </Box>
            )}{' '}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fieldWrapper: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
    },
    fieldName: {
      color: sharedColors.gray4,
      textTransform: 'uppercase',
      marginBottom: theme.spacing(0.2),
    },
    fieldValueWithUnit: {
      display: 'flex',
      flexDirection: 'row',
    },
    fieldUnit: {
      color: sharedColors.gray5,
      marginTop: 'auto',
      marginBottom: 'auto',
      marginLeft: theme.spacing(0.2),
    },
    grid: {
      flexGrow: 1,
    },
  }),
);
